import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../app/providers/AuthProvider';
import { Input } from '../components/Input';
import { PasswordInput } from '../components/PasswordInput';
import { Button } from '../components/Button';
import { Logo } from '../components/Logo';
import { COLORS } from '../../../core/constants/colors';
import { api } from '../../../core/utils/apiClient';

export const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const attemptsRef = useRef(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login({ email, password });
      
      // fetchAndStoreUserInfo is awaited in login(), so userInfo should be available
      // But checkAuthState might still be updating authState, so we'll check localStorage directly
      const performRedirect = async () => {
        const userInfo = localStorage.getItem('userInfo');
        
        if (userInfo) {
          try {
            const user = JSON.parse(userInfo);
            const isAdmin = user.permissions?.is_admin === true || 
                            user.groups?.includes('admin') || false;
            const isPartner = user.is_partner === true;
            setLoading(false);
            
            if (isAdmin) {
              navigate('/admin', { replace: true });
              return true;
            }
            
            // Check companies for all users (not just partners)
            try {
              const companiesResponse = await api.get('/api/v1/companies/my-companies');
              let companies: any[] = [];
              
              if (companiesResponse.ok) {
                const companiesData = await companiesResponse.json();
                companies = Array.isArray(companiesData) ? companiesData : (companiesData.companies || []);
              }
              
              // If no companies found, initialize company based on email domain
              if (!companies || companies.length === 0) {
                try {
                  const initResponse = await api.post('/api/v1/companies/init');
                  
                  // If init returns 200, we got a company; if 204, it's a public domain or no action
                  if (initResponse.status === 200) {
                    const initData = await initResponse.json();
                    const initializedCompany = initData.company || initData;
                    
                    // Refresh user info to get updated role/permissions
                    const meResponse = await api.get('/api/v1/auth/me');
                    if (meResponse.ok) {
                      const meData = await meResponse.json();
                      const updatedUser = {
                        ...user,
                        permissions: meData.permissions || user.permissions,
                        groups: meData.groups || user.groups,
                      };
                      localStorage.setItem('userInfo', JSON.stringify(updatedUser));
                      
                      const isUpdatedAdmin = updatedUser.permissions?.is_admin === true || 
                                            updatedUser.groups?.includes('admin') || false;
                      const isUpdatedPartner = updatedUser.permissions?.is_partner === true ||
                                               updatedUser.permissions?.is_company_user === true ||
                                               false;
                      
                      if (isUpdatedAdmin) {
                        navigate('/admin', { replace: true });
                        return true;
                      }
                      
                      if (isUpdatedPartner) {
                        // Partner: redirect to enrichment with company name prefilled
                        navigate('/enrichment', { 
                          replace: true,
                          state: { prefilledCompanyName: initializedCompany.name || '' }
                        });
                        return true;
                      } else {
                        // Normal user: redirect to user dashboard
                        navigate('/dashboard/query', { replace: true });
                        return true;
                      }
                    }
                  } else if (initResponse.status === 204) {
                    // Public domain or no action taken - check user role and redirect accordingly
                    const meResponse = await api.get('/api/v1/auth/me');
                    if (meResponse.ok) {
                      const meData = await meResponse.json();
                      const updatedUser = {
                        ...user,
                        permissions: meData.permissions || user.permissions,
                        groups: meData.groups || user.groups,
                      };
                      localStorage.setItem('userInfo', JSON.stringify(updatedUser));
                      
                      const isUpdatedAdmin = updatedUser.permissions?.is_admin === true || 
                                            updatedUser.groups?.includes('admin') || false;
                      const isUpdatedPartner = updatedUser.permissions?.is_partner === true ||
                                               updatedUser.permissions?.is_company_user === true ||
                                               false;
                      
                      if (isUpdatedAdmin) {
                        navigate('/admin', { replace: true });
                        return true;
                      }
                      
                      if (isUpdatedPartner) {
                        navigate('/enrichment', { replace: true });
                        return true;
                      } else {
                        navigate('/dashboard/query', { replace: true });
                        return true;
                      }
                    }
                  }
                } catch (initError: any) {
                  console.warn('Error initializing company:', initError);
                  // Fall through to default redirect
                }
              } else {
                // User has companies - check role via /api/v1/auth/me
                const meResponse = await api.get('/api/v1/auth/me');
                if (meResponse.ok) {
                  const meData = await meResponse.json();
                  const updatedUser = {
                    ...user,
                    permissions: meData.permissions || user.permissions,
                    groups: meData.groups || user.groups,
                  };
                  localStorage.setItem('userInfo', JSON.stringify(updatedUser));
                  
                  const isUpdatedAdmin = updatedUser.permissions?.is_admin === true || 
                                        updatedUser.groups?.includes('admin') || false;
                  const isUpdatedPartner = updatedUser.permissions?.is_partner === true ||
                                           updatedUser.permissions?.is_company_user === true ||
                                           false;
                  
                  if (isUpdatedAdmin) {
                    navigate('/admin', { replace: true });
                    return true;
                  }
                  
                  if (isUpdatedPartner) {
                  // Partner has a company, redirect to dashboard
                  navigate('/dashboard', { replace: true });
                    return true;
                  } else {
                    // Normal user: redirect to user dashboard
                    navigate('/dashboard/query', { replace: true });
                    return true;
                  }
                }
              }
            } catch (companyError: any) {
              console.warn('Error checking companies:', companyError);
              // Fall through to default redirect
            }
            
            // Default redirect - check role via /api/v1/auth/me
            try {
              const meResponse = await api.get('/api/v1/auth/me');
              if (meResponse.ok) {
                const meData = await meResponse.json();
                const updatedUser = {
                  ...user,
                  permissions: meData.permissions || user.permissions,
                  groups: meData.groups || user.groups,
                };
                localStorage.setItem('userInfo', JSON.stringify(updatedUser));
                
                const isUpdatedAdmin = updatedUser.permissions?.is_admin === true || 
                                      updatedUser.groups?.includes('admin') || false;
                const isUpdatedPartner = updatedUser.permissions?.is_partner === true ||
                                         updatedUser.permissions?.is_company_user === true ||
                                         false;
                
                if (isUpdatedAdmin) {
                  navigate('/admin', { replace: true });
                  return true;
                }
                
                if (isUpdatedPartner) {
                  navigate('/enrichment', { replace: true });
                  return true;
                } else {
                  navigate('/dashboard/query', { replace: true });
                  return true;
                }
              }
            } catch (meError: any) {
              console.warn('Error checking user role:', meError);
            }
            
            // Final fallback redirect
            if (isPartner) {
              navigate('/enrichment', { replace: true });
            } else {
              navigate('/dashboard/query', { replace: true });
            }
            return true;
          } catch (parseError) {
            console.error('Error parsing user info:', parseError);
            setLoading(false);
            navigate('/dashboard', { replace: true });
            return true;
          }
        }
        return false;
      };
      
      // Try immediately first (now async)
      const redirectResult = await performRedirect();
      if (!redirectResult) {
        // If not found, wait a bit and try again (in case of race condition)
        attemptsRef.current = 0;
        const maxAttempts = 10; // Max 1 second (10 * 100ms)
        
        const checkAndRedirect = async () => {
          attemptsRef.current++;
          const result = await performRedirect();
          if (result || attemptsRef.current >= maxAttempts) {
            if (attemptsRef.current >= maxAttempts) {
              // Fallback: redirect to dashboard
              console.warn('User info not found after login, redirecting to dashboard');
              setLoading(false);
              navigate('/dashboard', { replace: true });
            }
          } else {
            setTimeout(checkAndRedirect, 100);
          }
        };
        
        setTimeout(checkAndRedirect, 50);
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: COLORS.extended.cultured }}
    >
      <div className="fixed top-4 right-6">
        <a
          href="https://pi.vgg.app/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium hover:underline"
          style={{ color: '#6B7280' }}
        >
          Docs
        </a>
      </div>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
          <Logo />
          
          <div className="text-center mb-8">
            <h1
              className="text-3xl md:text-4xl font-bold mb-2"
              style={{ color: COLORS.primary.persianIndigo }}
            >
              Welcome Back
            </h1>
            <p
              className="text-sm"
              style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}
            >
              Powering Progress. Innovating access to credit.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
            />

            <PasswordInput
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />

            {error && (
              <div
                className="mb-4 p-3 rounded-lg"
                style={{ backgroundColor: COLORS.extended.redSalsa + '20' }}
              >
                <p className="text-sm" style={{ color: COLORS.extended.redSalsa }}>
                  {error}
                </p>
              </div>
            )}

            <Button type="submit" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/forgot-password')}
              className="text-sm hover:underline"
              style={{ color: COLORS.user.queryButton }}
            >
              Forgot password?
            </button>
          </div>

          <div className="mt-6 text-center">
            <span
              className="text-sm"
              style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}
            >
              Don't have an account?{' '}
            </span>
            <button
              onClick={() => navigate('/signup')}
              className="text-sm font-semibold hover:underline"
              style={{ color: COLORS.user.queryButton }}
            >
              Sign up
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};